import type { BaseMarker } from './markers/base/baseMarker'
import type { RectangleMarker } from './markers/rectangle/rectangleMarker'
import type { PolygonMarker } from './markers/polygon/polygonMarker'
import type { CircleHandle } from './markers/handles/circleHandle'
import type { SquareHandle } from './markers/handles/squareHandle'
import type { BezierLineMarker } from './markers/bezier/bezierLineMarker'
import { BezierAdjustmentHandle } from './markers/bezier/bezierAdjustmentHandle'
import { MARKER_CLASSES } from './markerArea'

interface Point {
  x: number
  y: number
}

interface ControlPoint {
  start: Point
  end: Point
}

interface Segment {
  start: Point
  end: Point
}

interface PolygonSegment extends Segment {
  isActive: boolean
  isHovered: boolean
}

interface MarkerConstructor {
  new (...args: any[]): BaseMarker<any, any, any, any>
  typeName: string // This is for the static property
}

interface MarkerState {
  typeName: string
  mode: MarkerModes
  strokeWeight?: number
  strokeColor?: string
  isDashed?: boolean
  dashLength?: number
  dashSpaceLength?: number
  opacity?: number
  meta?: { [key: string]: any }
}

interface RectangleMarkerState extends MarkerState {
  position: Point
  dimensions: { height: number; width: number }
}

interface PolygonMarkerState extends MarkerState {
  points: Array<Point>
  guidelineStrokeColor?: string
}

interface BezierLineMarkerState extends MarkerState {
  anchorPoints: AnchorPoints
  controlPoints: ControlPoints
}

type Marker = RectangleMarker | PolygonMarker | BezierLineMarker
type MarkerTypeName = keyof typeof MARKER_CLASSES
type AnchorPoints = Array<Point>
type ControlPoints = Array<ControlPoint>
type Handle = CircleHandle | SquareHandle | BezierAdjustmentHandle
type TransformMatrixType = [number, number, number, number, number, number]

enum Axis {
  X = 'x',
  Y = 'y',
}

enum MarkerModes {
  Create = 'create',
  Display = 'display',
  Edit = 'edit',
}

export type {
  AnchorPoints,
  ControlPoints,
  ControlPoint,
  Point,
  Segment,
  Handle,
  Marker,
  MarkerState,
  MarkerConstructor,
  MarkerTypeName,
  BaseMarker,
  RectangleMarkerState,
  PolygonMarkerState,
  BezierLineMarkerState,
  TransformMatrixType,
  PolygonSegment,
}

export { Axis, MarkerModes }
